import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from "prop-types";
import {useSelector, useDispatch} from "react-redux";
import * as Colors from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import {Tooltip} from "@mui/material";
import AnswerEditor from "../answers/AnswerEditorHook";
import TagSelectOrCreateHook from "../tags/TagSelectOrCreateHook";
import ArgoMetadataEditorHook from "../../common/ArgoMetadataEditorHook";
import ActionEditorHook from "../actions/ActionEditorHook";
import GroupEditor from "../groups/GroupEditorHook";
import {
    initializeQuestionEditor,
    updateQuestionTemplate,
    clearQuestionEditor,
    validateQuestionNameAndQualifierUnique,
    checkQuestionReference,
    checkQuestionVersion
} from "../../../actions/questionEditorActions";
import {expanderClear} from "../../../actions/expanderActions";
import {getQuestionTemplate} from "../../../actions/questionTemplateActions";
import {addAnswer, removeAnswer, setShowGroup} from "../../../actions/answerEditorActions";
import {createActionTemplate} from "../../../actions/actionTemplateActions";
import {createGroupTemplate, getGroupTemplate} from "../../../actions/groupTemplateActions";
import {getMetadataDefinitions} from "../../../actions/metadataDefinitionActions";
import {addDrawerNameToStack, removeDrawerNameToStack} from "../../../actions/drawerStackAction";
import {STATE_LIST} from "../../../constants/dataConstants";
import ArgoQuestionFormatDefinitionHook from "../../common/ArgoQuestionFormatDefinitionHook";
import ArgoQuestionFormatCustomHook from "../../common/ArgoQuestionFormatCustomHook";
import * as icon from "../../../constants/iconConstants";
import * as questionType from "../../../constants/questionTypeConstants";
import ArgoCheckboxHook from "../../common/ArgoCheckboxHook";
import ArgoToolbarHook from "../../common/ArgoToolbarHook";
import ArgoTextFieldSelectHook from "../../common/ArgoTextFieldSelectHook";
import ArgoTextFieldHook from "../../common/ArgoTextFieldHook";
import ArgoButtonHook from "../../common/ArgoButtonHook";
import ArgoLikertAnswerGeneratorHook from "../../common/ArgoLikertAnswerGeneratorHook";
import ArgoTextEntryWithCustomOverrideHook from "../../common/ArgoTextEntryWithCustomOverrideHook";
import ArgoPortalPreviewHook from "../../common/ArgoPortalPreviewHook";
import ArgoCustomLikertInputStylingHook from "../../common/ArgoCustomLikertInputStylingHook";
import * as validate from "../../../utilities/validate";
import * as metadataUtils from "../../../utilities/metadata";
import * as metadata from "../../../utilities/metadata";
import * as questionTypes from "../../../constants/questionTypeConstants";
import * as TEMPLATE from "../../../constants/templateConstants";
import * as metadataTemplateTypes from "../../../constants/metadataTemplateTypes";
import * as portalShared from "../../../portal/shared/metadataConstantsAndUtilities"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GroupViewerExpandableHook from "../groups/GroupViewerExpandableHook";
import ArgoReviewTemplateHook from "../../common/ArgoReviewTemplateHook";
import {notificationOpen} from "../../../actions/notificationActions";
import {templateSaveSucceeded} from "../../../actions/templateSaveActions";
import useDeferredTemplateSave from "../../../hooks/useDeferredTemplateSave";
import {createEditorOrigin, EDITOR_TYPES} from "../../../utilities/editorInstanceOwnership";
import * as portalTypes from "../../../portal/constants/actionConstants";


const QUESTION_TEMPLATE_MODE = false;

function QuestionEditor(props) {

    const {
        createMode = false,
        title = "",
        templateKey,
        templateId,
        cancel = () => {},
        save,
        createInitialTemplate,
        questionDepth = 0,
        calledFrom = "",
        isLikert = false,
        questionExpanderEdit = false,
        questionExpanderSetStateCallback = function () {},
        scrollTo = "",
        width = "100%",
        groupTemplateMode
    } = props;

    const dispatch = useDispatch();

    // Redux state selectors
    const template = useSelector(state => state.questionEditor[questionDepth]?.template);
    const templateOriginal = useSelector(state => state.questionTemplate[templateKey]);
    const options = useSelector(state => state.questionEditor[questionDepth]?.options);
    const answerOptionTemplates = useSelector(state => state.questionEditor[questionDepth]?.answerOptionTemplates);
    const metadataDefinition = useSelector(state => state.metadataDefinition[metadataTemplateTypes.QuestionTemplate]);
    const settings = useSelector(state => state.settings);
    const editorInstanceId = useSelector(state => state.questionEditor[questionDepth]?.instanceId);
    const editorOrigin = createEditorOrigin(EDITOR_TYPES.QUESTION, editorInstanceId, questionDepth);

    // Local state
    const [pageInit, setPageInit] = useState(false);
    const [nameErrorText, setNameErrorText] = useState(" ");
    const [metadataErrorText, setMetadataErrorTextLocal] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [toolbarErrorText, setToolbarErrorText] = useState("");
    const [answerIndex, setAnswerIndex] = useState(null);
    const [headerInitializer, setHeaderInitializer] = useState("");
    const [displayAsLikert, setDisplayAsLikert] = useState(false);
    const [addAnswerTemplate, setAddAnswerTemplate] = useState("1");
    const [typeDisplay, setTypeDisplay] = useState({...metadata.defaultCustomMetadataObj});
    const [nameDisplay, setNameDisplay] = useState({...metadata.defaultCustomMetadataObj});
    const [descDisplay, setDescDisplay] = useState({...metadata.defaultCustomMetadataObj});
    const [keyMetricCategories, setKeyMetricCategories] = useState([]);
    const [keyMetricCategorySelectedIndex, setKeyMetricCategorySelectedIndex] = useState(0);
    const [hideShowGroup, setHideShowGroup] = useState(false)
    const [displayKeyMetricCategory, setDisplayKeyMetricCategory] = useState([])
    const [disableKMC, setDisableKMC] = useState(true)
    const [showActionEditor, setShowActionEditor] = useState(false);
    const [showGroupEditor, setShowGroupEditor] = useState(false);
    const [metadataSet, setMetadataSet] = useState(false);
    const [initialScroll, setInitialScroll] = useState(false);

    // ADDED To support show groups

    const [openGroupReview, setOpenGroupReview] = useState(false);
    const [selectedGroupInfoId, setSelectedGroupInfoId] = useState("");
    const [selectedGroupInfoKey, setSelectedGroupInfoKey] = useState("");
    const [selectedGroupInfoTemplate, setSelectedGroupInfoTemplate] = useState(null);

    const [openQuestionReview, setOpenQuestionReview] = useState(false);
    const [selectedQuestionInfoId, setSelectedQuestionInfoId] = useState("");
    const [selectedQuestionInfoKey, setSelectedQuestionInfoKey] = useState("");
    const [selectedQuestionInfoTemplate, setSelectedQuestionInfoTemplate] = useState(null);


    // Page init function
    const pageInitFunc = useCallback((currTemplate, currMetadataDefinition) => {

        let tempDisplayAsLikert = false;

        let metadataObj = currTemplate?.metadata ? currTemplate?.metadata : {};

        let tempTypeDisplayObj = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.CUSTOM_DDO_QUE_FORMAT);
        let tempLabelDisplayObj = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.CUSTOM_DDO_QUE_LABEL);

        if (metadataObj.hasOwnProperty(portalShared.CUSTOM_DDO_QUE_FORMAT)) {
            tempTypeDisplayObj.selected = "custom";
            tempTypeDisplayObj.metadataValue = metadataObj[portalShared.CUSTOM_DDO_QUE_FORMAT];
            tempTypeDisplayObj.metadataValueObj = JSON.parse(tempTypeDisplayObj.metadataValue);

            tempDisplayAsLikert = (tempTypeDisplayObj.metadataValueObj.displayAs === "likert");
        }

        if (metadataObj.hasOwnProperty(portalShared.CUSTOM_DDO_QUE_LABEL)) {
            tempLabelDisplayObj.selected = "custom";
            tempLabelDisplayObj.metadataValue = metadataObj[portalShared.CUSTOM_DDO_QUE_LABEL];
            tempLabelDisplayObj.metadataValueObj = JSON.parse(tempLabelDisplayObj.metadataValue);
        }

        let tempNameDisplayObj = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.CUSTOM_DDO_NAME);
        let tempDescDisplayObj = metadata.setTextObjSelectedAndValueAndValueObj(metadataObj, portalShared.CUSTOM_DDO_DESC);

        // // Use the passed keyMetricCategories or current state
        // const currentKeyMetricCategories = keyMetricCategories.length > 0 ? keyMetricCategories : [];
        // if (metadataObj.hasOwnProperty(portalShared.KEY_METRIC_CATEGORY_QUESTION)) {
        //     tempKeyMetricCategorySelectedIndex = currentKeyMetricCategories.indexOf(metadataObj[portalShared.KEY_METRIC_CATEGORY_QUESTION]);
        // }

        setPageInit(true);
        setDisplayAsLikert(tempDisplayAsLikert);
        setTypeDisplay(metadata.setCustomMetadataObj(tempTypeDisplayObj));
        setNameDisplay(metadata.setCustomMetadataObj(tempNameDisplayObj));
        setDescDisplay(metadata.setCustomMetadataObj(tempDescDisplayObj));
        //setKeyMetricCategorySelectedIndex(tempKeyMetricCategorySelectedIndex);

    }, []);


    // componentDidMount equivalent
    useEffect(() => {

        dispatch(addDrawerNameToStack(createMode ? "Create" : "Edit", "Question"))

        // Get any preset metadata definitions for questions. ie REPORTING CATEGORIES (KEY METRICS)
        dispatch(getMetadataDefinitions(metadataTemplateTypes.QuestionTemplate));

        if (templateOriginal) {
            dispatch(initializeQuestionEditor(templateOriginal, questionDepth));
            dispatch(checkQuestionVersion(templateOriginal.name, questionDepth)); // Get version to determine if question type can be changed

            if (templateOriginal.questionType === questionTypes.LIKERT_RADIO) {
                // We get references for LIKERTS so if they belong to a group, then question type can NOT be changed
                dispatch(checkQuestionReference(templateOriginal.id, questionDepth));
            }

            pageInitFunc(templateOriginal, metadataDefinition);
        } else {
            // In the case where the question is edited from the group screen, group_template.questionKeys only has keys
            // So every group or that uses this question will reference the question_key/current
            if (templateKey || templateId) {
                setIsLoading(true);
                dispatch(getQuestionTemplate(templateKey, templateId));
            }
            else {
                dispatch(initializeQuestionEditor(createInitialTemplate, questionDepth));
            }
        }

        // Handle isLikert desc display
        if (isLikert) {
            setDescDisplay(prev => ({...prev, selected: "hide"}));
        } else {
            setDescDisplay(prev => ({...prev, selected: "default"}));
        }

        // Cleanup function (componentWillUnmount)
        return () => {
            dispatch(expanderClear("QuestionEditor"));
            dispatch(clearQuestionEditor(questionDepth));
            dispatch(removeDrawerNameToStack())
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle initial scroll
    useEffect(() => {

        if (!initialScroll) {
            let scrollToElement = (scrollTo === "") ? "QuestionEditor-height-div" : scrollTo;
            let page = document.getElementById(scrollToElement);
            if (page) {
                page.scrollIntoView({ block: 'end', behavior: 'smooth' });
                setInitialScroll(true);
            }
        }
    }, [initialScroll, scrollTo]);

    // Handle template loading (componentWillReceiveProps equivalent)
    useEffect(() => {

        if (!metadataSet && template && templateOriginal) {
            // if templateOriginal is loaded and the templateKey matches the current templateKey and is different from the loaded template
            const isTemplateLoaded = (templateOriginal && (templateOriginal.templateKey === templateKey) && (template.templateKey !== templateKey));

            // The loaded template is stale and the correct template is loaded
            const editTemplateReady = (template.id !== templateId) && (templateOriginal && templateOriginal.id === templateId);

            if (isTemplateLoaded && editTemplateReady) {
                setIsLoading(false);

                dispatch(initializeQuestionEditor(templateOriginal, questionDepth));
                dispatch(checkQuestionVersion(templateOriginal.name, questionDepth));

                if (templateOriginal.questionType === questionTypes.LIKERT_RADIO) {
                    dispatch(checkQuestionReference(templateOriginal.id, questionDepth));
                }

                pageInitFunc(templateOriginal, metadataDefinition);
            }
        }
    }, [template, templateOriginal, templateKey, templateId, metadataSet, questionDepth, dispatch, pageInitFunc, metadataDefinition]);

    useEffect(() => {
        // Get showGroupKeys templates

        // After creating a question with a nested show group, save then edit the length was throwing an error.
        if (templateOriginal && templateOriginal.hasOwnProperty("showGroupKeys") && templateOriginal.showGroupKeys && templateOriginal.showGroupKeys.hasOwnProperty("length")) {
            for (let i = 0; i < templateOriginal.showGroupKeys.length; i++) {
                dispatch(getGroupTemplate(templateOriginal.showGroupKeys[i]));
            }
        }

    }, [templateOriginal, dispatch]);

    // Handle metadata definitions loading
    useEffect(() => {

        if (template && !metadataSet && metadataDefinition && !metadataDefinition.hasOwnProperty("collection") && keyMetricCategories.length === 0) {
            let tempKeyMetricCategories = [];
            tempKeyMetricCategories.push("None");
            for (let i = 0; i < metadataDefinition.length; i++) {
                if (metadataDefinition[i].name === portalShared.KEY_METRIC_CATEGORY_QUESTION) {
                    tempKeyMetricCategories.push(...metadataDefinition[i].listValues);
                }
            }
            setKeyMetricCategories(tempKeyMetricCategories);
            setDisplayKeyMetricCategory(tempKeyMetricCategories)

            let metadataObj = template?.metadata ? template.metadata : {};

            // Use the passed keyMetricCategories or current state
            const currentKeyMetricCategories = tempKeyMetricCategories.length > 0 ? tempKeyMetricCategories : [];
            if (metadataObj.hasOwnProperty(portalShared.KEY_METRIC_CATEGORY_QUESTION)) {
                setKeyMetricCategorySelectedIndex(currentKeyMetricCategories.indexOf(metadataObj[portalShared.KEY_METRIC_CATEGORY_QUESTION]));
            } else {
                setKeyMetricCategorySelectedIndex(0);
            }

            // if (templateOriginal) {
            //     pageInitFunc(templateOriginal, metadataDefinition, );
            // }
        }
        template.metadata.hasOwnProperty(portalShared.DO_NOT_USE_SHOW_GROUP) ? setHideShowGroup(true) : setHideShowGroup(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metadataDefinition, metadataSet, template]);

    // Handle isLikert descDisplay update
    useEffect(() => {

        if (isLikert) {
            setDescDisplay(prev => ({...prev, selected: "hide"}));
        }
    }, [isLikert]);

    // Check for errors on screen
    useEffect(() => {

        if (template && answerOptionTemplates) {
            checkForErrorsOnScreen();
        }
    }, [template, answerOptionTemplates, options, metadataErrorText]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {

        if (calledFrom === TEMPLATE.TYPE.GROUP) {
            if (isLikert) {
                let typesList = [...questionTypes.questionTypesList];
                let index = typesList.findIndex(x => x.value ==="LIKERT_RADIO");
                setKeyMetricCategorySelectedIndex(index);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLikert]);

    // Allow child components to set errors on this page.
    const setStateCallback = (stateObj) => {
        let objKeyArray = Object.keys(stateObj);

        if (objKeyArray !== "null") {
            // Handle each state property
            if (stateObj.hasOwnProperty('pageInit')) setPageInit(stateObj.pageInit);
            if (stateObj.hasOwnProperty('nameErrorText')) setNameErrorText(stateObj.nameErrorText);
            if (stateObj.hasOwnProperty('metadataErrorText')) setMetadataErrorTextLocal(stateObj.metadataErrorText);
            if (stateObj.hasOwnProperty('isLoading')) setIsLoading(stateObj.isLoading);
            if (stateObj.hasOwnProperty('toolbarErrorText')) setToolbarErrorText(stateObj.toolbarErrorText);
            if (stateObj.hasOwnProperty('answerIndex')) setAnswerIndex(stateObj.answerIndex);
            if (stateObj.hasOwnProperty('headerInitializer')) setHeaderInitializer(stateObj.headerInitializer);
            if (stateObj.hasOwnProperty('displayAsLikert')) setDisplayAsLikert(stateObj.displayAsLikert);
            if (stateObj.hasOwnProperty('addAnswerTemplate')) setAddAnswerTemplate(stateObj.addAnswerTemplate);
            if (stateObj.hasOwnProperty('typeDisplay')) setTypeDisplay(stateObj.typeDisplay);
            if (stateObj.hasOwnProperty('nameDisplay')) setNameDisplay(stateObj.nameDisplay);
            if (stateObj.hasOwnProperty('descDisplay')) setDescDisplay(stateObj.descDisplay);
            if (stateObj.hasOwnProperty('keyMetricCategories')) setKeyMetricCategories(stateObj.keyMetricCategories);
            if (stateObj.hasOwnProperty('keyMetricCategorySelectedIndex')) setKeyMetricCategorySelectedIndex(stateObj.keyMetricCategorySelectedIndex);
            if (stateObj.hasOwnProperty('showActionEditor')) setShowActionEditor(stateObj.showActionEditor);
            if (stateObj.hasOwnProperty('showGroupEditor')) setShowGroupEditor(stateObj.showGroupEditor);
            if (stateObj.hasOwnProperty('metadataSet')) setMetadataSet(stateObj.metadataSet);
            if (stateObj.hasOwnProperty('initialScroll')) setInitialScroll(stateObj.initialScroll);

            // ADDED To support show groups

            if (stateObj.hasOwnProperty('openGroupReview')) setOpenGroupReview(stateObj.openGroupReview);
            if (stateObj.hasOwnProperty('selectedGroupInfoId')) setSelectedGroupInfoId(stateObj.selectedGroupInfoId);
            if (stateObj.hasOwnProperty('selectedGroupInfoKey')) setSelectedGroupInfoKey(stateObj.selectedGroupInfoKey);
            if (stateObj.hasOwnProperty('selectedGroupInfoTemplate')) setSelectedGroupInfoTemplate(stateObj.selectedGroupInfoTemplate);

            if (stateObj.hasOwnProperty('openQuestionReview')) setOpenQuestionReview(stateObj.openQuestionReview);
            if (stateObj.hasOwnProperty('selectedQuestionInfoId')) setSelectedQuestionInfoId(stateObj.selectedQuestionInfoId);
            if (stateObj.hasOwnProperty('selectedQuestionInfoKey')) setSelectedQuestionInfoKey(stateObj.selectedQuestionInfoKey);
            if (stateObj.hasOwnProperty('selectedQuestionInfoTemplate')) setSelectedQuestionInfoTemplate(stateObj.selectedQuestionInfoTemplate);

        }
    };

    const saveQuestion = useCallback(() => {
        let templateToSave = null;
        if (template.hasOwnProperty(templateKey)) {
            templateToSave = {
                ...template[templateKey],
                answerOptionTemplates
            };
        }
        else {
            templateToSave = {
                ...template,
                answerOptionTemplates
            };
        }

        if (questionExpanderEdit) {
            return questionExpanderSetStateCallback({questionTemplate: templateToSave}, true, TEMPLATE.TYPE.QUESTION);
        }

        return save(templateToSave);
    }, [answerOptionTemplates, questionExpanderEdit, questionExpanderSetStateCallback, save, template, templateKey]);

    const notifyDeferredSave = useCallback(() => {
        dispatch(notificationOpen("Waiting for the new template to finish saving…"));
    }, [dispatch]);
    const saveQuestionWhenReady = useDeferredTemplateSave(saveQuestion, notifyDeferredSave);

    const handleToolbarButtonClick = (event, fieldName, buttonTitle) => {
        switch (buttonTitle) {
            case "CANCEL":
                cancel();
                break;
            case "SAVE":
                saveQuestionWhenReady();
                break;
            case "BACK":
                setOpenGroupReview(false);
                setOpenQuestionReview(false);
                break;
            // no default
        }

        event.preventDefault(); // Fix for the enter key propagating to the next focusable element
    };

    const onTextChange = (event, fieldName) => {
        let value = event.target.value;

        if ((fieldName === "nameQualifier") && (value === "")) {
            value = null;
        }

        let updatedTemplate = {...template};
        updatedTemplate[fieldName] = value;

        dispatch(updateQuestionTemplate(updatedTemplate));

        if (fieldName === "name" || fieldName === "nameQualifier") {
            dispatch(validateQuestionNameAndQualifierUnique(updatedTemplate.name, updatedTemplate.nameQualifier, questionDepth));
        }
    };

    const onSelectChange = (event, fieldName) => {
        let selected = event.target.value;

        switch (fieldName) {
            case "questionType":
                handleInputChangeAndUpdateTemplate(event, fieldName, selected);
                break;
            case "keyMetricCategory":
                let index = event.target.value;
                selected = keyMetricCategories[index];  // Note: "None: has been added to the metadata list keyMetricCategories, so metadata needs to adjust

                setKeyMetricCategorySelectedIndex(index);
                metadataUtils.setCustomMetadata(null, template, (updatedTemplate) => dispatch(updateQuestionTemplate(updatedTemplate)), setStateCallback, fieldName, selected, portalShared.KEY_METRIC_CATEGORY_QUESTION, selected, questionDepth);
                break;
            default:
                setStateCallback({[fieldName]: selected});
                break;
        }
    };

    const onCheckBoxChange = (event, fieldName) => {
        let value = event.target.checked;

        if (fieldName === "doNotUseShowGroup") {
            metadataUtils.setCustomMetadata(null,
                template, (updatedTemplate) => dispatch(updateQuestionTemplate(updatedTemplate)),
                setStateCallback,
                fieldName,
                value || "default",
                portalShared.DO_NOT_USE_SHOW_GROUP,
                value || "default",
                questionDepth
            );
        }
        else {
            let updatedTemplate = {...template};
            updatedTemplate[fieldName] = !value;

            dispatch(updateQuestionTemplate(updatedTemplate, questionDepth));
        }
    };

    // Handle change will work for any UI input, autocomplete, text, select, checkbox...
    const handleInputChangeAndUpdateTemplate = (event, fieldName, key) => {
        let value = event.target.value;

        let updatedTemplate = {...template};
        updatedTemplate[fieldName] = value;

        switch (fieldName) {
            case "questionType":
                // Question type is changed so delete all answers prev. added
                for (let aa = answerOptionTemplates.length - 1; aa >= 0; aa--) {
                    dispatch(removeAnswer(QUESTION_TEMPLATE_MODE, aa));
                }

                // Question type is changed so delete all metadata added
                let tempMetadata = {};

                for (key in updatedTemplate.metadata) {
                    if (key === portalShared.CUSTOM_DDO_NAME || key === portalShared.CUSTOM_DDO_DESC || key === portalShared.KEY_METRIC_CATEGORY_QUESTION) {
                        tempMetadata[key] = updatedTemplate.metadata[key];
                    }
                }
                updatedTemplate.metadata = tempMetadata; // Remove all metadata except those above

                let newDescDisplay = {...descDisplay};

                switch (value) {
                    case questionTypes.YES_NO:
                        // Add Yes/No Answers
                        dispatch(addAnswer(QUESTION_TEMPLATE_MODE, {optionText: "Yes", metadata: {[icon.ICON_POSITION]: icon.POSITION_NONE}, index: 0}, questionDepth));
                        dispatch(addAnswer(QUESTION_TEMPLATE_MODE, {optionText: "No", metadata: {[icon.ICON_POSITION]: icon.POSITION_NONE}, index: 1}, questionDepth));
                        break;
                    case questionTypes.ADDRESS_FULL:
                    case questionTypes.STATE_ONLY:
                        // Add state answers
                        STATE_LIST.forEach((item, index) => {
                            dispatch(addAnswer(QUESTION_TEMPLATE_MODE, {optionText: item, index: index}, questionDepth));
                        });
                        break;
                    case questionTypes.LIKERT_RADIO:
                        newDescDisplay.selected = "hide";
                        break;
                    // no default
                }

                dispatch({type: portalTypes.INCREMENT_PORTAL_COUNT});
                setTypeDisplay({...metadata.defaultCustomMetadataObj});
                setDescDisplay(newDescDisplay);
                break;

            case "name":
            case "nameQualifier":
                // Check that name/qualifier is not a duplicate
                dispatch(validateQuestionNameAndQualifierUnique(updatedTemplate.name, updatedTemplate.nameQualifier, questionDepth));
                break;
            // no default
        }

        dispatch(updateQuestionTemplate(updatedTemplate, questionDepth));
    };

    const onButtonClickAddAnswer = () => {
        let addCount = parseInt(addAnswerTemplate);

        for (let i = 0; i < addCount; i++) {
            // Per Cameron, answers added to pre-existing questions, then answer titles should be blank. NOTE: Answer titles are required to save.
            if (template.questionType === questionType.RADIO) {
                if (answerOptionTemplates.length > 0)
                    dispatch(addAnswer(QUESTION_TEMPLATE_MODE, {optionText: "", metadata: {[icon.ICON_POSITION]: icon.POSITION_NONE}, index: answerOptionTemplates.length + i}, questionDepth));
                else
                    dispatch(addAnswer(QUESTION_TEMPLATE_MODE, {optionText: (answerOptionTemplates.length + i + 1).toString(), metadata: {[icon.ICON_POSITION]: icon.POSITION_NONE}, index: answerOptionTemplates.length + i}, questionDepth));
            }
            else {
                if (answerOptionTemplates.length > 0)
                    dispatch(addAnswer(QUESTION_TEMPLATE_MODE, {optionText: "", index: answerOptionTemplates.length + i}, questionDepth));
                else
                    dispatch(addAnswer(QUESTION_TEMPLATE_MODE, {optionText: (answerOptionTemplates.length + i + 1).toString(), index: answerOptionTemplates.length + i}, questionDepth));
            }
        }
    };

    const metadataObj = (template && template.metadata) ? template.metadata : {};

    let propsObj = {
        fileName: "QuestionEditor",
        onTextChange: onTextChange,
        templateType: TEMPLATE.TYPE.QUESTION,
        template: template,
        metadata: metadataObj,
        answerOptionTemplates: answerOptionTemplates,
        templateUpdateFunction: (updatedTemplate, depth) => dispatch(updateQuestionTemplate(updatedTemplate, depth)),
        setStateCallbackFunction: setStateCallback,
        setCustomMetadataFunction: metadataUtils.setCustomMetadata,
        depth: questionDepth,
        displayAs: "radios",
        customObj: null
    };

    if (propsObj.metadata.hasOwnProperty(portalShared.CUSTOM_DDO_QUE_FORMAT)) {
        propsObj.customObj = JSON.parse(propsObj.metadata[portalShared.CUSTOM_DDO_QUE_FORMAT]);
        propsObj.displayAs = propsObj.customObj.displayAs;
    }

    useEffect(() => {
        if ([3, 5, 7].includes(propsObj.answerOptionTemplates.length)) {
            setDisplayKeyMetricCategory([...(keyMetricCategories.filter(cat => cat !== "Net Promoter Score (NPS)"))])
            setDisableKMC(false)
            return
        }

        if (propsObj.answerOptionTemplates.length === 11) {
            setDisplayKeyMetricCategory([keyMetricCategories[0], ...(keyMetricCategories.filter(cat => cat === "Net Promoter Score (NPS)"))])
            setDisableKMC(false)
            return
        }

        if (propsObj?.template?.questionType === "LIKERT_RADIO") {
            setDisplayKeyMetricCategory(keyMetricCategories)
            setDisableKMC(false)
            return
        }

        setDisplayKeyMetricCategory(keyMetricCategories)
        setDisableKMC(true)
    }, [keyMetricCategories, propsObj.answerOptionTemplates.length, propsObj?.template?.questionType])

    const isStateOrAddressQuestionType = () => {
        let type = template.questionType;
        return (type === questionTypes.STATE_ONLY || type === questionTypes.ADDRESS_FULL);
    };

    const isYesNoQuestionType = () => {
        let type = template.questionType;
        return (type === questionTypes.YES_NO);
    };

    const questionTypeHasAnswerOptions = (qType) => {
        switch (qType) {
            case questionTypes.YES_NO:
            case questionTypes.RADIO:
            case questionTypes.CHECKBOX:
            case questionTypes.ADDRESS_FULL:
            case questionTypes.STATE_ONLY:
            case questionTypes.RANKING:
                return true;
            default:
                return false;
        }
    };

    const answerOptionEditor = (answerOptionTemplatesParam, propsObj) => {
        let isLikertDisplay = displayAsLikert;

        if (template.metadata.hasOwnProperty(portalShared.CUSTOM_DDO_QUE_FORMAT)) {
            let metadataValue = JSON.parse(template.metadata[portalShared.CUSTOM_DDO_QUE_FORMAT]);

            if (metadataValue.hasOwnProperty("displayAs")) {
                isLikertDisplay = (metadataValue.displayAs === "likert");
            }
        }

        // NOTE: Must pass in answerOptionTemplates from render so this function gets called if answerOptionTemplates changes
        // If NO-Answers, Then which answer generator to use, Non-LIKERT or LIKERT, If LIKERT and there are answers then [REMOVE ANSWERS] button will display

        // If valid question type for answers and non-LIKERT display answers and [ADD ANSWERS] button, also NOTE: Yes/No questions have their answers automatically generated
        if (questionTypeHasAnswerOptions(template.questionType) && !isLikertDisplay) {
            return (
                <div id="QuestionEditor-non-likert-answer-generator-container-div" style={{width: "100%", minHeight: "min-content", paddingTop: "4px"}}>
                    <div>
                        <div id="QuestionEditor-answer-editor-container-div">

                            <AnswerEditor
                                parentName={"QuestionEditor"}
                                disableRemoveIcon={isStateOrAddressQuestionType() || isYesNoQuestionType()}
                                displayAsLikert={isLikertDisplay}
                                isLikertAnswer={false}
                                setMetadataErrorText={setMetadataErrorText}
                                hasAnswers={questionTypeHasAnswerOptions(template.questionType)}
                                template={template}
                                answerOptionTemplates={answerOptionTemplatesParam}
                                questionDepth={questionDepth}
                                setStateCallback={setStateCallback}
                                groupTemplateMode={groupTemplateMode}
                                createMode={createMode}
                            />

                        </div>
                        <div>
                            {/* Note radio entries have an answer generator section, EXCEPT Yes/No, address or state which are automatically generated */}

                            {(isStateOrAddressQuestionType() || isYesNoQuestionType()) ? "" :

                                <div id="QuestionEditor-non-likert-answer-generator-container-div" style={{display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "16px", marginTop: "16px"}}>

                                    <div id="QuestionEditor-generate-answers-button" style={{alignItems: "left", paddingTop: '14px'}}>

                                        <ArgoButtonHook
                                            fileName="QuestionEditor"
                                            fieldName="add-answers"
                                            variant="contained"
                                            label={addAnswerTemplate === '1' ? "Add an answer" : "Add Answers"}
                                            paddingTop="0px"
                                            width={150}
                                            containedTextColor={'white'}
                                            containedBgColor={Colors.blue[500]}
                                            containedHoverTextColor={'#010203'}
                                            containedHoverBgColor={Colors.blue[500]}
                                            onClick={onButtonClickAddAnswer}
                                        />
                                        <span className="notranslate">​</span>

                                    </div>

                                    <div style={{alignItems: "left", width: "20px"}}></div>

                                    <div id="QuestionEditor-select-number-of-answers-to-generate" style={{alignItems: "left"}}>

                                        <ArgoTextFieldSelectHook
                                            fileName="QuestionEditor"
                                            fieldName="addAnswerTemplate"
                                            label="Template"
                                            value={addAnswerTemplate}
                                            onChange={onSelectChange}
                                            width="200px"
                                            menuItems={[
                                                {value: "1", text: "Single Answer"},
                                                {value: "2", text: "2 Answers"},
                                                {value: "3", text: "3 Answers"},
                                                {value: "4", text: "4 Answers"},
                                                {value: "5", text: "5 Answers"},
                                                {value: "6", text: "6 Answers"},
                                                {value: "7", text: "7 Answers"},
                                                {value: "8", text: "8 Answers"}
                                            ]}
                                        />

                                    </div>

                                </div>
                            }
                        </div>
                    </div>
                </div>
            );
        }
        else {
            return (
                <div id="QuestionEditor-ArgoLikertAnswerGenerator-container-div">

                    {isLikertDisplay ?
                        <ArgoLikertAnswerGeneratorHook
                            fileName="QuestionEditor"
                            componentMode="likert-answers"
                            templateType={TEMPLATE.TYPE.QUESTION}
                            templateTextType={TEMPLATE.TEXT_TYPE.QUESTION.LIKERT_TITLES}
                            template={template}
                            isLikert={true}
                            displayAsLikert={isLikertDisplay}
                            answerOptionTemplates={answerOptionTemplatesParam}
                            answerErrorCallback={() => {}}
                            templateUpdateFunction={(updatedTemplate, depth) => dispatch(updateQuestionTemplate(updatedTemplate, depth))}
                            setMetadataErrorText={setMetadataErrorText}
                            questionDepth={questionDepth}
                            setStateCallback={setStateCallback}
                            customMetadataKey={portalShared.CUSTOM_DDO_QUE_FORMAT}
                            propsObj={propsObj}
                            createMode={createMode}
                        /> : ""
                    }
                </div>
            );
        }
    };

    const checkForErrorsOnScreen = () => {
        // Questions editor drawer can have 5 types of errors.
        // name is blank or duplicate name/nameQualifier
        // question type requires answers and there are none
        // note: answers can have blank optionText or metadata with no key, The answer editor will do those checks and use setStateCallback to set error on this drawer
        // note: answer editor will also display error text on the answers with blank optionText or metadata with bank keys
        // metadata key is blank
        // note: metadata editor will display an error message on metadata entry with the missing key

        let tempNameErrorText = validate.templateNameAndQualifierUnique({template, options}, "Question Name");
        let tempMetadataErrorText = metadataErrorText;
        let tempToolbarErrorText = "";
        let duplicateAnswersErrorText = "";
        let blankAnswerErrorText = "";

        if (tempNameErrorText !== " ") {
            tempToolbarErrorText = tempNameErrorText;
        }

        if (tempMetadataErrorText !== "") {
            tempToolbarErrorText = tempToolbarErrorText === "" ? tempMetadataErrorText : tempToolbarErrorText + ",  " + tempMetadataErrorText;
        }

        // Note: Specific answer errors i.e. missing answer optionText or answer metadata errors are checked by answerEditor
        // Note: Requires a callback to answerEditor to disable review button and set toolbar error message
        if (questionTypeHasAnswerOptions(template.questionType) && answerOptionTemplates.length === 0) {
            let needAnswersErrorText = "Answers are required for this question type!";
            tempToolbarErrorText = tempToolbarErrorText === "" ? needAnswersErrorText : tempToolbarErrorText + ",  " + needAnswersErrorText;
        }

        if (answerOptionTemplates.reduce((isFilled, option) => {
            if (!isFilled) isFilled = option.optionText.length === 0;
            return isFilled;
        }, false)) {
            blankAnswerErrorText = "One or more answers is blank";
        }

        if (blankAnswerErrorText !== "") {
            tempToolbarErrorText = tempToolbarErrorText === "" ? blankAnswerErrorText : tempToolbarErrorText + ",  " + blankAnswerErrorText;
        }

        for (let i = 0; i < answerOptionTemplates.length; i++) {
            const answerOption = answerOptionTemplates[i].optionText;
            for (let j = 0; j < answerOptionTemplates.length; j++) {
                const compareTo = answerOptionTemplates[j].optionText;
                if (answerOption === compareTo && i !== j) {
                    duplicateAnswersErrorText = "Duplicate answer names!";
                }
            }
        }

        if (duplicateAnswersErrorText !== "") {
            tempToolbarErrorText = tempToolbarErrorText === "" ? duplicateAnswersErrorText : tempToolbarErrorText + ",  " + duplicateAnswersErrorText;
        }

        setNameErrorText(tempNameErrorText);
        setToolbarErrorText(tempToolbarErrorText);
    };

    const setMetadataErrorText = (errorText) => {
        // NOTE: setState callback is not working, state is NOT being set before call, so added fix
        setMetadataErrorTextLocal(errorText);
        setMetadataSet(false);
    };

    const metadataChanged = (updatedTemplate, depth) => {
        // Note: Timing issues was causing componentWillReceiveProps to trigger and a setState was canceling another setState
        setMetadataSet(true);
        dispatch(updateQuestionTemplate(updatedTemplate, depth));
    };

    // -----------------------------------------------------------------------------------------------------------------
    // ACTION EDITOR ---------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    const saveAction = (actionTemplate) => {
        return dispatch(createActionTemplate(
            {...actionTemplate, answerIndex},
            editorOrigin
        )).then(result => {
            if (templateSaveSucceeded(result)) {
                setShowActionEditor(false);
                setAnswerIndex(null);
                setHeaderInitializer("");
            }
            return result;
        });
    };

    const closeActionEditor = () => {
        setShowActionEditor(false);
        setAnswerIndex(null);
        setHeaderInitializer("");
    };

    const actionEditor = () => {
        let actionInitialTemplate = {
            name: headerInitializer
        };

        return (
            <div className="layout vertical" style={{height: "100%"}}>
                <div className="layout vertical" style={{height: "100%"}}>
                    <ActionEditorHook
                        save={saveAction}
                        cancel={closeActionEditor}
                        createInitialTemplate={actionInitialTemplate}
                        showCreateToggle={() => {}}
                        createMode={true}
                        width={width}
                    />
                </div>
            </div>
        );
    };

    // -----------------------------------------------------------------------------------------------------------------
    // SHOW GROUP EDITOR -----------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    const closeShowGroupEditor = () => {
        setShowGroupEditor(false);
        setAnswerIndex(null);
    };

    const saveShowGroup = (groupTemplate) => {
        return dispatch(createGroupTemplate(
            {...groupTemplate, answerIndex},
            editorOrigin
        )).then(result => {
            if (templateSaveSucceeded(result)) {
                dispatch(setShowGroup(true, answerIndex, editorOrigin));
                setShowGroupEditor(false);
                setAnswerIndex(null);
            }
            return result;
        });
    };

    const groupEditorComponent = () => {
        return (
            <div className="layout vertical" style={{height: "100%"}}>
                <div className="layout vertical" style={{height: "100%"}}>
                    <GroupEditor
                        createMode={true}
                        save={saveShowGroup}
                        cancel={closeShowGroupEditor}
                        templateMode={"Create Group"}
                        parentEditorOrigin={editorOrigin}
                        width={width}
                        scrollTo="QuestionEditor-container-outer-div"
                    />
                </div>
            </div>
        );
    };

    // -----------------------------------------------------------------------------------------------------------------
    // RENDER ----------------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    if (isLoading) {
        return null;
    }

    if (showActionEditor) {
        return actionEditor();
    }

    if (showGroupEditor) {
        return groupEditorComponent();
    }

    // Guard against undefined template
    if (!template) {
        return null;
    }

    let questionTypesList = [...questionTypes.questionTypesList];

    // Check to see if question type can be changed
    let questionTypeChangeAllowed = false;
    if (createMode) {
        if (calledFrom === TEMPLATE.TYPE.GROUP) {
            if (isLikert) {
                questionTypeChangeAllowed = false;
            } else {
                let index = questionTypesList.findIndex(x => x.value ==="LIKERT_RADIO");
                if (index > 0) {
                    questionTypesList.splice(index,1); // Remove Likert option
                }
                questionTypeChangeAllowed = true;
            }
        } else {
            questionTypeChangeAllowed = true;
        }
    }
    else {
        if (isLikert) {
            questionTypeChangeAllowed = false;  // If being created from group LIKERT then question type can only be LIKERT
        } else {
            if (options?.hasVersion !== null) {
                questionTypeChangeAllowed = options.hasVersion.length === 1 && options.hasVersion[0].publishedDate == null;
            }
            if (template.questionType === questionTypes.LIKERT_RADIO) {
                questionTypeChangeAllowed = questionTypeChangeAllowed && options?.hasReference && options.hasReference.length === 0;
            }
        }
    }

    if (openGroupReview) {
        return <ArgoReviewTemplateHook
            templateType={TEMPLATE.TYPE.GROUP}
            templateId={selectedGroupInfoId}
            templateKey={selectedGroupInfoKey}
            selectedTemplate={selectedGroupInfoTemplate}
            selectedTemplateIsGraph={false}
            handleToolbarButtonClickFunction={handleToolbarButtonClick}
            width={props.width}
        />;
    }

    if (openQuestionReview) {
        return (
            <ArgoReviewTemplateHook
                templateType={TEMPLATE.TYPE.QUESTION}
                templateKey={selectedQuestionInfoKey}
                templateId={selectedQuestionInfoId}
                templateObject={selectedQuestionInfoTemplate}
                handleToolbarButtonClickFunction={handleToolbarButtonClick}
                width={props.width}
            />
        );
    }

    return (
        pageInit || createMode ?
            <div id="QuestionEditor-container-outer-div" style={{height: "100%"}}>

                <ArgoToolbarHook
                    fileName="QuestionEditor"
                    fieldName="save-or-cancel"
                    toolbarErrorText={toolbarErrorText}
                    isValidating={options?.isValidating}
                    width={width}
                    onClick={handleToolbarButtonClick}
                />
                <div id="QuestionEditor-height-div" style={{height: "50px"}}></div>

                <div id="QuestionEditor-page-div" style={{height: "100%"}}>

                    <div id="QuestionEditor-container-div"
                         style={{flexGrow: "2", paddingLeft: "32px", paddingRight: "32px"}}>

                        <div>
                            {title === "" ? "" :
                                <div>
                                    <div>&nbsp;</div>
                                    <Typography style={{fontWeight: "bold"}}>{title}</Typography>
                                </div>
                            }
                        </div>

                        <div>&nbsp;</div>

                        <div style={{display: "flex", alignItems: "left", width: "100%"}}>

                            <ArgoTextFieldSelectHook
                                fileName="QuestionEditor"
                                fieldName="questionType"
                                label="Question Type"
                                value={template.questionType}
                                onChange={onSelectChange}
                                menuItems={questionTypesList}
                                disabled={!questionTypeChangeAllowed}
                            />

                            <ArgoQuestionFormatDefinitionHook
                                questionType={template.questionType}
                                fieldName="typeDisplay"
                                fieldTextObjName="typeDisplay"
                                initTo={typeDisplay.selected}
                                metadataDetails
                                metadataPropsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_QUE_FORMAT}, propsObj)}
                            />
                        </div>

                        <div>&nbsp;</div>

                        {(typeDisplay.selected === 'custom') ?
                            <ArgoQuestionFormatCustomHook
                                questionType={template.questionType}
                                fieldName="typeDisplay"
                                fieldTextObjName="typeDisplay"
                                initToObj={typeDisplay.metadataValueObj}
                                answerCount={answerOptionTemplates.length}
                                answers={answerOptionTemplates}
                                metadataPropsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_QUE_FORMAT}, propsObj)}
                            />
                            :
                            <div></div>
                        }

                        <div>&nbsp;</div>

                        <ArgoTextEntryWithCustomOverrideHook
                            templateType={TEMPLATE.TYPE.QUESTION}
                            textEntryType={TEMPLATE.TEXT_TYPE.QUESTION.NAME}
                            fieldName="name"
                            fieldTextObjName="nameDisplay"
                            index={100}
                            label="Question Name"
                            labelCustom="Display (Text)"
                            value={template.name}
                            maxLength={2000}
                            errorText={nameErrorText}
                            initTo={nameDisplay.selected}
                            initToObj={nameDisplay.metadataValueObj}
                            propsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_NAME}, propsObj)}
                            showQuestionNum={true}
                            autoFocus={true}
                        />

                        <div>&nbsp;</div>

                        <div id="QuestionEditor-qualifier-container-div"
                             style={{display: "flex", alignItems: "left", width: "100%"}}>

                            <ArgoTextFieldHook
                                fileName="QuestionEditor"
                                fieldName="nameQualifier"
                                label="Name Qualifier"
                                value={template.nameQualifier}
                                maxLength={255}
                                multiline={true}
                                onChange={onTextChange}
                                width="96%"
                                divContainerHeight="52px"
                            />

                            <div style={{width: "83px"}}>&nbsp;</div>
                        </div>

                        <div>&nbsp;</div>

                        <ArgoTextEntryWithCustomOverrideHook
                            templateType={TEMPLATE.TYPE.QUESTION}
                            textEntryType={TEMPLATE.TEXT_TYPE.QUESTION.DESC}
                            fieldName="description"
                            fieldTextObjName="descDisplay"
                            index={200}
                            label="Description/Instructions"
                            labelCustom="Display (Description)"
                            value={template.description}
                            maxLength={255}
                            initTo={descDisplay.selected}
                            initToObj={descDisplay.metadataValueObj}
                            hideOnly={template.questionType === "LIKERT_RADIO"}
                            propsObj={Object.assign({metadataTag: portalShared.CUSTOM_DDO_DESC}, propsObj)}
                            showIndent={true}
                        />

                        <ArgoTextFieldSelectHook
                            fileName="QuestionEditor"
                            fieldName="keyMetricCategory"
                            label="Key Metric Classification"
                            value={keyMetricCategorySelectedIndex}
                            onChange={onSelectChange}
                            width="350px"
                            menuItems={displayKeyMetricCategory}
                            disabled={disableKMC}
                            icon={
                                <Tooltip title={`Key Metric Categories are assigned to questions with 1-3, 1-5, 1-7 or 0-10 answers.`}>
                                    <InfoOutlinedIcon style={{fontSize: "16px"}}/>
                                </Tooltip>
                            }
                        />

                        <div>&nbsp;</div>

                        {/*// ===========================================================================================================*/}
                        {/*// OPTIONAL QUESTION */}
                        {/*// ===========================================================================================================*/}

                        <div style={{display: "flex"}}>
                            {settings?.showQuestionRequired ?
                                <ArgoCheckboxHook
                                    fileName="QuestionEditor"
                                    fieldName="optional"
                                    label={"Required Question"}
                                    labelColor={template.optional ? "black" : "red"}
                                    onChange={onCheckBoxChange}
                                    checked={!template.optional}
                                    paddingTop="-2px"
                                    bold={true}
                                    width="200px"
                                /> : ""
                            }

                            {template.questionType === questionTypes.LIKERT_RADIO ?
                                <ArgoCheckboxHook
                                    fileName="QuestionEditor"
                                    fieldName="doNotUseShowGroup"
                                    label={"Disable Show Group"}
                                    labelColor={"black"}
                                    onChange={onCheckBoxChange}
                                    checked={hideShowGroup}
                                    paddingTop="-2px"
                                    bold={true}
                                    width="200px"
                                /> : ""
                            }
                        </div>

                        {/*// ===========================================================================================================*/}
                        {/*// TAGS */}
                        {/*// ===========================================================================================================*/}

                        <div>&nbsp;</div>

                        <div style={{fontSize: "large", fontWeight: "bold", paddingBottom: "24px"}}>Tags</div>

                        <TagSelectOrCreateHook questionDepth={questionDepth} selectedTags={template.searchTags}/>

                        {/*// ===========================================================================================================*/}
                        {/*// METADATA EDITOR */}
                        {/*// ===========================================================================================================*/}

                        <div id="QuestionEditor-metadata-container-div" style={{width: "98%", marginLeft: "24px"}}>
                            <ArgoMetadataEditorHook
                                entityType={metadataTemplateTypes.QuestionTemplate}
                                template={template}
                                onChangeCallback={metadataChanged}
                                depth={questionDepth}
                                metadata={template.metadata}
                                errorText={metadataErrorText}
                                setMetadataErrorText={setMetadataErrorText}
                                hidden={true}
                            />
                        </div>

                        {/*// ===========================================================================================================*/}
                        {/*// ASSIGNED ANSWERS LIST */}
                        {/*// ===========================================================================================================*/}

                        {answerOptionEditor(answerOptionTemplates, propsObj)}

                        {/*// ===========================================================================================================*/}
                        {/*// LIKERT ANSWER CUSTOMIZATION */}
                        {/*// ===========================================================================================================*/}

                        {(propsObj.displayAs === "likert") && (answerOptionTemplates.length > 0) ?
                            <ArgoCustomLikertInputStylingHook
                                templateType={TEMPLATE.TYPE.QUESTION}
                                template={template}
                                depth={questionDepth}
                                customMetadataTag={portalShared.CUSTOM_DDO_QUE_FORMAT}
                                templateUpdateFunction={(updatedTemplate) => dispatch(updateQuestionTemplate(updatedTemplate))}
                                inputType={answerOptionTemplates[0].metadata[icon.ICON_TYPE]}
                                propsObj={propsObj}
                            />
                            : ""}

                        {/*// ===========================================================================================================*/}
                        {/*// RELATED SHOW GROUPS LIST */}
                        {/*// ===========================================================================================================*/}

                        <GroupViewerExpandableHook
                            parentName="QuestionEditor"
                            parentKey=""
                            // categoryTemplate={template}
                            groupKeys={createMode ? (template && template.hasOwnProperty("showGroupKeys") ? template.showGroupKeys : []) : ((templateOriginal && templateOriginal.hasOwnProperty("showGroupKeys")) ? templateOriginal.showGroupKeys : [])}
                            setStateCallback={setStateCallback}
                            hidePortalPreview={true}
                            questionTemplate={templateOriginal}
                            showIconButtonReorder={false}
                            showIconButtonEdit={false}
                            showIconButtonRemove={false}
                            isShowGroups={true}
                        />

                        {/*// ===========================================================================================================*/}
                        {/*// PORTAL PREVIEW */}
                        {/*// ===========================================================================================================*/}

                        <div id="QuestionEditor-space-above-portal-preview" style={{height: "20px"}}>&nbsp;</div>

                        <ArgoPortalPreviewHook
                            calledBy="QuestionEditor"
                            createMode={createMode}
                            templateType={TEMPLATE.TYPE.QUESTION}
                            template={template}
                            answerOptionTemplates={answerOptionTemplates}
                            hidePortalPreview={template.questionType === questionType.LIKERT_RADIO}
                        />

                    </div>
                </div>
            </div> : <div id="QuestionEditor-container-outer-div"/>
    );
}


QuestionEditor.defaultProps = {
    createMode: false,
    title: "",
    template: undefined,
    cancel: () => {},
    showMetaData: false,
    isLikert: false,
    row: null,
    questionExpanderEdit: false,
    questionExpanderSetStateCallback: function () {},
    questionDepth: 0,
    scrollTo: "",
    width: "100%"
};

QuestionEditor.propTypes = {
    scrollTo: PropTypes.string,
    createMode: PropTypes.bool,
    title: PropTypes.string,
    templateKey: PropTypes.string,
    templateId: PropTypes.number,
    row: PropTypes.object,
    cancel: PropTypes.func,
    save: PropTypes.func,
    showMetaData: PropTypes.bool,
    groupTemplateList: PropTypes.array,
    createInitialTemplate: PropTypes.object,
    questionDepth: PropTypes.number,
    isLikert: PropTypes.bool,
    questionExpanderEdit: PropTypes.bool,
    questionExpanderSetStateCallback: PropTypes.func,
    width: PropTypes.string,
    groupTemplateMode: PropTypes.bool
};

export default QuestionEditor;
